import { useEffect, useRef, useState } from 'react';
import axios from 'axios';

// Where test-mode sends are redirected. Kept in sync with TEST_RECIPIENT
// on the statements controller — purely informational here for UI text.
const TEST_DESTINATION = 'david@xsitegroup.ca';

// Single-step send flow: generate the PDF on open, present FILE,
// ARCHIVE, RECIPIENT and SUBJECT for review, then send via Resend.
// Mirrors the final review step of QuoteDialog — no intermediate Word
// edit, no metadata grid. Test Mode defaults ON so a real client
// can't accidentally receive a statement during development.
export default function StatementDialog({ open, record, onClose, onSent }) {
  // step: 'generating' | 'ready' | 'sent'
  const [step, setStep] = useState('generating');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [generated, setGenerated] = useState(null);  // server response from /generate
  const [sent, setSent] = useState(null);            // server response from /send
  const [recipient, setRecipient] = useState('');
  const [subject, setSubject] = useState('');
  const [testMode, setTestMode] = useState(true);
  // Blob URL for the inline PDF preview. Fetched via axios so the
  // JWT header is included, then wrapped in URL.createObjectURL so
  // the iframe can render it without re-authenticating.
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const generateOnce = useRef(false);

  // Auto-fire generate as soon as the dialog opens. The ref guard
  // prevents a double-submit if React re-runs the effect (StrictMode,
  // prop churn) before /generate resolves.
  useEffect(() => {
    if (!open) {
      generateOnce.current = false;
      return;
    }
    setStep('generating');
    setBusy(false);
    setError(null);
    setGenerated(null);
    setSent(null);
    setRecipient(record?.email1 || '');
    setSubject('');
    setTestMode(true);
    setPdfUrl(null);
    setPdfLoading(false);

    if (generateOnce.current) return;
    generateOnce.current = true;

    (async () => {
      setBusy(true);
      try {
        const res = await axios.post('/api/statements/generate', { contact_id: record.id });
        setGenerated(res.data);
        setRecipient(prev => prev || res.data?.default_recipient || '');
        setSubject(res.data?.default_subject || '');
        setStep('ready');

        // Fetch the rendered PDF for the inline preview. Done as a
        // blob so the authenticated axios instance carries the JWT
        // — an iframe-src GET wouldn't include the bearer header.
        setPdfLoading(true);
        try {
          const pdfRes = await axios.get(
            `/api/statements/${res.data.document_id}/pdf`,
            { responseType: 'blob' }
          );
          const blob = new Blob([pdfRes.data], { type: 'application/pdf' });
          setPdfUrl(URL.createObjectURL(blob));
        } catch (pdfErr) {
          console.warn('Statement preview load failed:', pdfErr.message);
        } finally {
          setPdfLoading(false);
        }
      } catch (err) {
        setError(err.response?.data?.error || err.message);
      } finally {
        setBusy(false);
      }
    })();
  }, [open, record]);

  // Release the object URL when the dialog unmounts or the URL is
  // replaced — otherwise Chrome keeps the blob alive for the lifetime
  // of the document.
  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, busy]);

  if (!open) return null;

  async function handleSend() {
    if (!generated) return;
    setError(null);
    if (!recipient.trim()) {
      setError('Recipient email is required.');
      return;
    }
    if (!subject.trim()) {
      setError('Subject is required.');
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(`/api/statements/${generated.document_id}/send`, {
        to: recipient.trim(),
        company: generated.company,
        subject: subject.trim(),
        test_mode: testMode
      });
      setSent(res.data);
      setStep('sent');
      if (onSent) onSent();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="modal-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="modal-card modal-wide" role="dialog" aria-modal="true" aria-label="Generate Account Statement">
        <header className="modal-head">
          <h3>
            {step === 'generating' && 'Generating Account Statement…'}
            {step === 'ready' && 'Review & Send Statement'}
            {step === 'sent' && 'Statement Sent'}
          </h3>
          <button type="button" className="modal-close" onClick={onClose} disabled={busy} aria-label="Close">×</button>
        </header>

        {step === 'generating' && (
          <div className="modal-body">
            <p className="modal-sub">
              Compiling the PDF account statement for
              {' '}<strong>{record?.company || 'this customer'}</strong>…
            </p>
            {error && <p className="error">{error}</p>}
            {error && (
              <div className="form-actions" style={{ marginTop: 16 }}>
                <button type="button" className="btn-secondary" onClick={onClose}>Close</button>
              </div>
            )}
          </div>
        )}

        {step === 'ready' && generated && (
          <div className="modal-body">
            <p className="modal-sub">
              PDF ready. Review the document below, confirm the
              recipient and subject, then send. Leaving Test Mode on
              redirects the email to {TEST_DESTINATION} and skips the
              auto-note on the customer record.
            </p>

            {/* Inline PDF preview. The blob URL is created from an
                authenticated fetch so the iframe can render it
                without re-authenticating. */}
            <div className="statement-preview">
              {pdfLoading && !pdfUrl && (
                <div className="statement-preview-empty">Loading preview…</div>
              )}
              {!pdfLoading && !pdfUrl && (
                <div className="statement-preview-empty">
                  Preview unavailable. The PDF was generated and can still be sent.
                </div>
              )}
              {pdfUrl && (
                <iframe
                  src={pdfUrl}
                  title="Account statement preview"
                  className="statement-preview-frame"
                />
              )}
            </div>

            {error && <p className="error">{error}</p>}

            <div className="form-grid">
              <label className="form-field form-field-wide">
                <span>File</span>
                <input
                  type="text"
                  value={generated.file_path || ''}
                  readOnly
                  className="cell-mono"
                />
              </label>
              <label className="form-field form-field-wide">
                <span>Archive</span>
                <input
                  type="text"
                  value={generated.archive_pdf_path || '(network archive unavailable)'}
                  readOnly
                  className="cell-mono"
                />
              </label>
              <label className="form-field form-field-wide">
                <span>Recipient</span>
                <input
                  type="email"
                  value={recipient}
                  onChange={(e) => setRecipient(e.target.value)}
                  placeholder="client@example.com"
                  className="cell-mono"
                />
              </label>
              <label className="form-field form-field-wide">
                <span>Subject</span>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="XSite Account Statement - Company"
                />
              </label>
            </div>

            <div className={`test-mode-panel test-mode-${testMode ? 'on' : 'off'}`}>
              <label className="test-mode-toggle">
                <input
                  type="checkbox"
                  checked={testMode}
                  onChange={(e) => setTestMode(e.target.checked)}
                  disabled={busy}
                />
                <span className="test-mode-label">Test Mode</span>
              </label>
              {testMode ? (
                <p className="test-mode-status">
                  Statement will be redirected to <strong>{TEST_DESTINATION}</strong> instead of
                  <code> {recipient || '(no recipient)'}</code>. No note logged on the customer record.
                </p>
              ) : (
                <p className="test-mode-status">
                  <strong>LIVE</strong> — statement will be emailed to the real client at
                  <code> {recipient || '(no recipient)'}</code> and a note will be logged on the record.
                </p>
              )}
            </div>

            <div className="form-actions" style={{ marginTop: 16 }}>
              <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
                Close (don't send)
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleSend}
                disabled={busy || !recipient.trim() || !subject.trim()}
              >
                {busy
                  ? 'Sending…'
                  : testMode
                    ? 'Send Test Statement'
                    : 'Send Statement (LIVE)'}
              </button>
            </div>
          </div>
        )}

        {step === 'sent' && sent && (
          <div className="modal-body">
            {sent.test_mode && (
              <p
                className="error"
                style={{
                  background: 'var(--color-warn-bg)',
                  color: 'var(--color-warn)',
                  borderColor: 'var(--color-warn-border)'
                }}
              >
                <strong>TEST MODE active</strong> — server redirected the email
                from <code>{sent.intended_to}</code> to <code>{sent.to}</code>.
                No note was logged on the customer record.
              </p>
            )}
            <div className="quote-success">
              <div className="quote-success-icon">✓</div>
              <h4>{sent.test_mode ? 'Test Statement Sent' : 'Statement Sent'}</h4>
              <p>The PDF has been emailed to <strong>{sent.to}</strong>.</p>
              <dl className="quote-success-meta">
                <div><dt>Subject</dt><dd>{sent.subject}</dd></div>
                <div><dt>Resend ID</dt><dd className="cell-mono">{sent.resend_message_id || '—'}</dd></div>
                <div><dt>Source File</dt><dd className="cell-mono">{sent.file_path}</dd></div>
                <div><dt>Sent At</dt><dd className="cell-mono">{new Date(sent.sent_at).toLocaleString()}</dd></div>
              </dl>
              <div className="form-actions">
                <button type="button" className="btn-primary" onClick={onClose}>Done</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
